/**
 * File Upload Component with Drag & Drop
 * Supports images, PDFs, and other files
 */

import React, { useRef, useState, useCallback } from "react";
import { Upload, X, File as FileIcon, Loader, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Progress } from "../ui/progress";
import toast from "react-hot-toast";
import cmsApiService from "../../services/cmsApiService";

interface FileUploadProps {
  value?: string | File;
  onChange: (file: File | string) => void;
  onRemove?: () => void;
  folder?: string;
  accept?: string; // e.g., "image/*", ".pdf", "image/*,.pdf"
  maxSize?: number; // in MB
  label?: string;
  description?: string;
  className?: string;
  showPreview?: boolean;
  uploadOnSelect?: boolean; // Auto-upload when file selected
  variant?: "image" | "file"; // Visual variant
  showRemoveButton?: boolean; // Show/hide the X remove button in preview
}

export const FileUpload: React.FC<FileUploadProps> = ({
  value,
  onChange,
  onRemove,
  folder = "uploads",
  accept = "*/*",
  maxSize = 10,
  label = "Upload File",
  description,
  className = "",
  showPreview = true,
  uploadOnSelect = false,
  variant = "file",
  showRemoveButton = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preview, setPreview] = useState<string | undefined>(
    typeof value === "string" ? value : undefined,
  );
  const [selectedFile, setSelectedFile] = useState<File | undefined>(
    value instanceof File ? value : undefined,
  );

  // Sync internal state with value prop changes (e.g., when parent clears the value)
  React.useEffect(() => {
    if (typeof value === "string") {
      // If value is empty, clear state; otherwise set the preview
      if (!value) {
        setPreview(undefined);
        setSelectedFile(undefined);
      } else {
        setPreview(value);
      }
    } else if (value instanceof File) {
      setSelectedFile(value);
    } else if (!value) {
      setPreview(undefined);
      setSelectedFile(undefined);
    }
  }, [value]);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      return {
        valid: false,
        error: `File size must be less than ${maxSize}MB`,
      };
    }

    // Check file type if accept is specified
    if (accept !== "*/*") {
      const acceptTypes = accept.split(",").map((t) => t.trim());
      const fileType = file.type;
      const fileName = file.name;

      const isValid = acceptTypes.some((acceptType) => {
        if (acceptType.startsWith(".")) {
          // Extension check
          return fileName.endsWith(acceptType);
        } else if (acceptType.includes("/*")) {
          // MIME type wildcard check (e.g., "image/*")
          const baseType = acceptType.split("/")[0];
          return fileType.startsWith(baseType + "/");
        } else {
          // Exact MIME type check
          return fileType === acceptType;
        }
      });

      if (!isValid) {
        return {
          valid: false,
          error: `File type not accepted. Allowed: ${accept}`,
        };
      }
    }

    return { valid: true };
  };

  const createPreview = (file: File) => {
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else if (file.type === "application/pdf") {
      setPreview("pdf");
    } else {
      setPreview("file");
    }
  };

  const handleFileSelect = async (file: File) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error || "Invalid file");
      return;
    }

    setSelectedFile(file);
    createPreview(file);

    if (uploadOnSelect) {
      await uploadFile(file);
    } else {
      onChange(file);
    }
  };

  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Simulate progress (since axios doesn't provide real progress in this setup)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      const response = await cmsApiService.uploadImage(file, folder);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Response contains the URL
      const fileUrl = response.url || response.imageUrl || response;
      onChange(fileUrl);

      toast.success("File uploaded successfully");

      setTimeout(() => setUploadProgress(0), 1000);
    } catch (error) {
      toast.error("Failed to upload file");
      setPreview(undefined);
      setSelectedFile(undefined);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  const handleRemove = () => {
    setPreview(undefined);
    setSelectedFile(undefined);
    onChange("");
    if (onRemove) {
      onRemove();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setUploadProgress(0);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const renderPreview = () => {
    if (!showPreview || !preview) return null;

    if (variant === "image" && preview !== "pdf" && preview !== "file") {
      return (
        <div className="relative group">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-md"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center gap-2">
            <Button
              type="button"
              className="text-white border border-white"
              size="sm"
              onClick={handleClick}
              disabled={isUploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Change
            </Button>
            <Button
              type="button"
              variant="red"
              size="sm"
              onClick={handleRemove}
              disabled={isUploading}
            >
              <X className="h-4 w-4 mr-2" />
              Remove
            </Button>
          </div>
        </div>
      );
    }

    // File preview (PDF, documents, etc.)
    return (
      <div className="flex items-center justify-between p-4 border rounded-md bg-muted/50">
        <div className="flex items-center gap-3">
          <FileIcon className="h-8 w-8 text-primary" />
          <div>
            <p className="text-sm font-medium">
              {selectedFile?.name || "Uploaded file"}
            </p>
            <p className="text-xs text-muted-foreground">
              {selectedFile &&
                `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Loader className="h-4 w-4 animate-spin text-primary" />
          )}
          {uploadProgress === 100 && (
            <Check className="h-4 w-4 text-green-500" />
          )}
          {showRemoveButton && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={isUploading}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-foreground flex items-center gap-2">
          {label}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <span className="text-xs text-muted-foreground">
              ({uploadProgress}%)
            </span>
          )}
        </label>
      )}

      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {preview || selectedFile ? (
        <div className="space-y-2">
          {renderPreview()}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Progress value={uploadProgress} className="h-2" />
          )}
        </div>
      ) : (
        <Card
          className={`border-2 transition-all ${
            isDragging
              ? "border-primary bg-primary/5"
              : "border-dashed border-muted-foreground/20 hover:border-primary/50"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CardContent className="p-6">
            <button
              type="button"
              onClick={handleClick}
              disabled={isUploading}
              className="w-full flex flex-col items-center justify-center gap-3 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <Loader className="h-12 w-12 animate-spin text-primary" />
                  <div className="text-center">
                    <p className="text-sm font-medium">Uploading...</p>
                    {uploadProgress > 0 && (
                      <Progress
                        value={uploadProgress}
                        className="h-2 w-48 mx-auto mt-2"
                      />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="relative">
                    <Upload className="h-12 w-12" />
                    {isDragging && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Check className="h-8 w-8 text-primary animate-pulse" />
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium">
                      {isDragging
                        ? "Drop file here"
                        : "Click to upload or drag and drop"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Max size: {maxSize}MB
                      {accept !== "*/*" &&
                        ` • ${accept.replace(/\*/g, "all types")}`}
                    </p>
                  </div>
                </>
              )}
            </button>
          </CardContent>
        </Card>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        className="hidden"
      />

      {isUploading && uploadProgress > 0 && uploadProgress < 100 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader className="h-3 w-3 animate-spin" />
          <span>Uploading file...</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
